import { NgModule } from '@angular/core';
import { RouterModule, type Routes } from '@angular/router';

import { AuthGuard } from './authentication/_guards/auth.guard';
import { AuthLayoutComponent } from './shared/_layouts/auth-layout/auth-layout.component';
import { FullComponent } from './shared/_layouts/full/full.component';
import { ComingSoonComponent } from './shared/coming-soon/coming-soon';
import { AccessDeniedComponent } from './shared/components/access-denied/access-denied.component';

const routes: Routes = [
  // Redirect root to login
  { path: '', redirectTo: '/login', pathMatch: 'full' },

  // Authentication routes
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./authentication/authentication.module').then(m => m.AuthenticationModule)
      }
    ]
  },
  { path: '', component: FullComponent, children: [{ path: 'access-denied', component: AccessDeniedComponent }] },

  // Main application routes
  {
    path: '',
    canActivate: [AuthGuard],
    component: FullComponent,
    children: [
      {
        path: 'logs', // Do not move this path, it must be the first child of FullComponent
        loadChildren: () => import('./modules/logs/log-file.module').then(m => m.LogModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ApplicationSupport']
        }
      },
      {
        path: 'master-data/translations',
        loadChildren: () => import('./modules/master-data/translations/translation.module').then(m => m.TranslationModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ApplicationSupport']
        }
      },
      {
        path: 'change-password',
        loadChildren: () => import('./change-password/change-password.module').then(m => m.ChangePasswordModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Owner', 'ROLE_Staff', 'ROLE_ApplicationSupport', 'ROLE_Staff_With_RestrictedAssetAccess']
          roles: []
        }
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ClientAdmin', 'Owner', 'Staff', 'Staff_With_RestrictedAssetAccess']
          roles: ['AssetRead', 'SecuredAssetRead', 'EventRead']
        }
      },
      {
        path: 'coming-soon',
        component: ComingSoonComponent,
        canMatch: [AuthGuard],
        data: { roles: [] }
      },
      {
        path: 'assets/history',
        loadChildren: () => import('./modules/asset-history/asset-history.module').then(m => m.AssetHistoryModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Staff']
          roles: ['AssetRead']
        }
      },
      {
        path: 'events',
        loadChildren: () => import('./modules/events/event.module').then(m => m.EventModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Staff', 'ROLE_Staff_With_RestrictedAssetAccess']
          roles: ['EventRead']
        }
      },
      {
        path: 'master-data/asset-codes',
        loadChildren: () => import('./modules/master-data/asset-codes/asset-code.module').then(m => m.AssetCodeModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SecuredAssetRead']
        }
      },
      {
        path: 'master-data/asset-types',
        loadChildren: () => import('./modules/master-data/asset-types/asset-type.module').then(m => m.AssetTypeModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Owner']
          roles: ['SimpleMasterDataRead', 'SecuredAssetRead']
        }
      },
      {
        path: 'master-data/asset-statuses',
        loadChildren: () => import('./modules/master-data/asset-statuses/asset-status.module').then(m => m.AssetStatusModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/asset-locations',
        loadChildren: () => import('./modules/master-data/asset-locations/asset-location.module').then(m => m.AssetLocationModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['LocationRead']
        }
      },
      {
        path: 'master-data/event-statuses',
        loadChildren: () => import('./modules/master-data/event-statuses/event-status.module').then(m => m.EventStatusModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/event-templates',
        loadChildren: () => import('./modules/master-data/event-templates/event-template.module').then(m => m.EventTemplateModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['EventTemplateRead']
        }
      },
      {
        path: 'master-data/event-template-types',
        loadChildren: () => import('./modules/master-data/event-template-types/event-template-type.module').then(m => m.EventTemplateTypeModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/sites',
        loadChildren: () => import('./modules/master-data/sites/site.module').then(m => m.SiteModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/states',
        loadChildren: () => import('./modules/master-data/states/state.module').then(m => m.StateModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/countries',
        loadChildren: () => import('./modules/master-data/countries/country.module').then(m => m.CountryModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/currencies',
        loadChildren: () => import('./modules/master-data/currencies/currency.module').then(m => m.CurrencyModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'roles',
        loadChildren: () => import('./modules/user-management/roles/role.module').then(m => m.RoleModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'users',
        loadChildren: () => import('./modules/user-management/users/user.module').then(m => m.UserModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Owner']
          roles: []
        }
      },
      {
        path: 'notifications',
        loadChildren: () => import('./modules/notifications/notification.module').then(m => m.NotificationModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Owner', 'ROLE_Staff', 'ROLE_Staff_With_RestrictedAssetAccess']
          roles: []
        }
      },
      {
        path: 'gifts',
        loadChildren: () => import('./modules/gifts/gifts.module').then(m => m.GiftsModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['GiftRead']
        }
      },
      {
        path: 'vendors',
        loadChildren: () => import('./modules/vendors/vendors.module').then(m => m.VendorsModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['VendorRead']
        }
      },
      {
        path: 'master-data/notification-types',
        loadChildren: () => import('./modules/master-data/notification-types/notification-types.module').then(m => m.NotificationTypesModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/units',
        loadChildren: () => import('./modules/master-data/units/unit.module').then(m => m.UnitModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/reference-types',
        loadChildren: () => import('./modules/master-data/reference-types/reference-type.module').then(m => m.ReferenceTypeModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/reference-values',
        loadChildren: () => import('./modules/master-data/reference-values/reference-value.module').then(m => m.ReferenceValueModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: '', // Do not move this path, it must be the last known child of FullComponent
        loadChildren: () => import('./modules/assets/asset.module').then(m => m.AssetModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin', 'ROLE_Owner', 'ROLE_Staff', 'ROLE_Staff_With_RestrictedAssetAccess']
          roles: ['AssetRead', 'SecuredAssetRead']
        }
      },

      // Redirect unknown routes within the FullComponent layout to 'dashboard'
      { path: '**', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },

  // Wildcard route for undefined paths
  // This should be placed at the end
  { path: '**', redirectTo: '/login', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
